"use client";

import { cn } from "@/lib/cn";
import { useSelectedItem } from "../../hooks/useSelectedItem";
import { POIContent } from "./POIContent";
import { POIHeader } from "./POIHeader";

/**
 * Whether the POI details are showing.
 *
 * Exported so the surfaces that make room for the panel — the sidebar, which the
 * panel replaces — decide from the same condition the panel itself uses, instead
 * of each testing its own approximation and disagreeing for a frame.
 *
 * It keys on the loaded item rather than on the selected id: `useItem` keeps the
 * previous POI while the next one loads, so the panel never blanks between
 * selections, and a selection that resolves to nothing leaves the results up.
 */
export function useIsPOIDetailsOpen(): boolean {
  const { data } = useSelectedItem();
  return !!data;
}

/** The POI details panel. Renders nothing when no POI is open. */
export function POIDetails({ className }: { className?: string }) {
  const isOpen = useIsPOIDetailsOpen();
  const { data: item, error } = useSelectedItem();

  // Only reachable where the panel is mounted whether or not a POI resolved —
  // the mobile full-screen view. The desktop sidebar swaps to the panel only once
  // there is an item, so a failed fetch there simply leaves the results up.
  if (error && !item) {
    return (
      <div
        role="alert"
        className={cn("bg-white p-3 text-sm text-red-600", className)}
      >
        {error.message}
      </div>
    );
  }

  if (!isOpen || !item) return null;

  return (
    <div
      className={cn(
        "POIPage flex h-full flex-col bg-white pb-2 dark:bg-dark-color",
        className
      )}
    >
      <POIHeader />
      <POIContent item={item} />
    </div>
  );
}
